import React, { useEffect, useState } from "react";
import { Plus, Pencil, ShieldCheck, Power, Trash2, UserPlus, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError, fmtDate } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { PageHeader, Panel } from "../lib/ui";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";

export default function Users() {
  const { can, user } = useAuth();
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [divisions, setDivisions] = useState([]);
  const [divOpen, setDivOpen] = useState(false);
  const [divEdit, setDivEdit] = useState(null);
  const [logs, setLogs] = useState([]);
  const [permKeys, setPermKeys] = useState([]);
  const [open, setOpen] = useState(false);
  const [edit, setEdit] = useState(null);
  const canManageDiv = can("accounting", "supervisor", "production_spv");
  const canPush = !!(user && (user.is_owner || (user.effective_roles || [user.role]).includes("admin")));
  const [pushing, setPushing] = useState(null);

  const pushVente = async (u) => {
    if (!window.confirm(`Buat akun ${u.name} di Vente People?`)) return;
    setPushing(u.id);
    try {
      const { data } = await api.post(`/users/${u.id}/push-to-vente-people`);
      if (data.status === "exists") {
        toast.info(data.message || "Sudah terdaftar di Vente People");
      } else {
        toast.success(data.message || "Berhasil dibuat di Vente People");
        if (data.needs_profile_completion) toast.info("Profil perlu dilengkapi di Vente People");
      }
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setPushing(null);
    }
  };

  const delUser = async (u) => {
    if (!window.confirm(`Hapus pengguna "${u.name}" (${u.email})? Tindakan ini permanen.`)) return;
    try { await api.delete(`/users/${u.id}`); toast.success("Pengguna dihapus"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const load = () => {
    api.get("/users").then((r) => setUsers(r.data));
    api.get("/users/roles").then((r) => setRoles(r.data));
    api.get("/rab/divisions").then((r) => setDivisions(r.data));
    api.get("/users/audit-logs").then((r) => setLogs(r.data)).catch(() => {});
    api.get("/roles/catalog-v2").then((r) => setPermKeys((r.data.permissions || []).map((p) => p.key))).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const toggleDivision = async (d) => {
    try {
      await api.put(`/rab/divisions/${d.id}`, { name: d.name, code: d.code, active: !d.active });
      toast.success(d.active ? "Divisi dinonaktifkan" : "Divisi diaktifkan");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div data-testid="users-page">
      <PageHeader title="Users & Roles" subtitle="Kelola pengguna dan hak akses">
        {can() && <Button onClick={() => { setEdit(null); setOpen(true); }} data-testid="add-user-btn" className="bg-cocoa hover:bg-cocoa-dark text-ivory"><Plus className="w-4 h-4 mr-1.5" /> Pengguna</Button>}
      </PageHeader>

      <Tabs defaultValue="users">
        <TabsList className="bg-white border border-taupe/20">
          <TabsTrigger value="users" data-testid="tab-users">Pengguna</TabsTrigger>
          <TabsTrigger value="audit" data-testid="tab-audit">Audit Log</TabsTrigger>
          <TabsTrigger value="divisions" data-testid="tab-divisions">Divisi</TabsTrigger>
        </TabsList>

        <TabsContent value="users">
          <Panel>
            <Table>
              <TableHeader>
                <TableRow className="hover:bg-transparent">
                  <TableHead>Nama</TableHead><TableHead>Email</TableHead>
                  <TableHead>Peran</TableHead><TableHead>Divisi</TableHead><TableHead>Status</TableHead>
                  <TableHead className="text-right">Aksi</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {users.map((u) => (
                  <TableRow key={u.id} data-testid={`user-row-${u.id}`}>
                    <TableCell className="font-semibold text-cocoa">{u.name}</TableCell>
                    <TableCell>{u.email}</TableCell>
                    <TableCell><span className="inline-flex items-center gap-1 text-sm"><ShieldCheck className="w-3.5 h-3.5 text-taupe" />{u.role_label}</span></TableCell>
                    <TableCell>{u.division_name}</TableCell>
                    <TableCell><span className={u.active ? "text-status-passed" : "text-wmuted"}>{u.active ? "Aktif" : "Nonaktif"}</span></TableCell>
                    <TableCell className="text-right">
                      {can() && <Button size="icon" variant="ghost" onClick={() => { setEdit(u); setOpen(true); }} data-testid={`edit-user-${u.id}`}><Pencil className="w-4 h-4 text-cocoa" /></Button>}
                      {canPush && <Button size="icon" variant="ghost" disabled={pushing === u.id} title={u.vente_people_synced_at ? "Sudah terkirim ke Vente People — kirim ulang" : "Kirim ke Vente People"} onClick={() => pushVente(u)} data-testid={`push-vente-people-${u.id}`}>{pushing === u.id ? <Loader2 className="w-4 h-4 animate-spin text-taupe" /> : <UserPlus className={`w-4 h-4 ${u.vente_people_synced_at ? "text-status-passed/60" : "text-cocoa"}`} />}</Button>}
                      {can() && <Button size="icon" variant="ghost" onClick={() => delUser(u)} data-testid={`delete-user-${u.id}`}><Trash2 className="w-4 h-4 text-status-reject" /></Button>}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Panel>
        </TabsContent>

        <TabsContent value="audit">
          <Panel>
            <Table>
              <TableHeader>
                <TableRow className="hover:bg-transparent">
                  <TableHead>Waktu</TableHead><TableHead>Pengguna</TableHead>
                  <TableHead>Aksi</TableHead><TableHead>Entitas</TableHead><TableHead>Detail</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {logs.map((l) => (
                  <TableRow key={l.id}>
                    <TableCell className="text-xs text-wmuted whitespace-nowrap">{fmtDate(l.datetime)}</TableCell>
                    <TableCell className="text-sm">{l.user_name}</TableCell>
                    <TableCell className="text-sm font-medium text-cocoa">{l.action}</TableCell>
                    <TableCell className="text-sm">{l.entity}</TableCell>
                    <TableCell className="text-xs text-wmuted font-mono">{JSON.stringify(l.details)}</TableCell>
                  </TableRow>
                ))}
                {!logs.length && <TableRow><TableCell colSpan={5} className="text-center text-wmuted py-10">Belum ada aktivitas.</TableCell></TableRow>}
              </TableBody>
            </Table>
          </Panel>
        </TabsContent>
        <TabsContent value="divisions">
          <Panel>
            <div className="flex justify-end p-3">
              {canManageDiv && <Button size="sm" data-testid="add-division-btn" onClick={() => { setDivEdit(null); setDivOpen(true); }} className="bg-cocoa hover:bg-cocoa-dark text-ivory"><Plus className="w-4 h-4 mr-1.5" /> Divisi</Button>}
            </div>
            <Table>
              <TableHeader>
                <TableRow className="hover:bg-transparent">
                  <TableHead>Nama</TableHead><TableHead>Kode</TableHead><TableHead>Status</TableHead>
                  <TableHead className="text-right">Aksi</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {divisions.map((d) => (
                  <TableRow key={d.id} data-testid={`division-row-${d.id}`}>
                    <TableCell className="font-semibold text-cocoa">{d.name}</TableCell>
                    <TableCell className="font-mono text-sm">{d.code}</TableCell>
                    <TableCell><span className={d.active ? "text-status-passed" : "text-wmuted"}>{d.active ? "Aktif" : "Nonaktif"}</span></TableCell>
                    <TableCell className="text-right">
                      {canManageDiv && <>
                        <Button size="icon" variant="ghost" data-testid={`edit-division-${d.id}`} onClick={() => { setDivEdit(d); setDivOpen(true); }}><Pencil className="w-4 h-4 text-cocoa" /></Button>
                        <Button size="icon" variant="ghost" data-testid={`toggle-division-${d.id}`} onClick={() => toggleDivision(d)}><Power className={`w-4 h-4 ${d.active ? "text-status-passed" : "text-wmuted"}`} /></Button>
                      </>}
                    </TableCell>
                  </TableRow>
                ))}
                {!divisions.length && <TableRow><TableCell colSpan={4} className="text-center text-wmuted py-10">Belum ada divisi.</TableCell></TableRow>}
              </TableBody>
            </Table>
          </Panel>
        </TabsContent>
      </Tabs>

      <UserDialog open={open} setOpen={setOpen} edit={edit} roles={roles} divisions={divisions} permKeys={permKeys} onDone={load} />
      <DivisionDialog open={divOpen} setOpen={setDivOpen} edit={divEdit} onDone={load} />
    </div>
  );
}

function DivisionDialog({ open, setOpen, edit, onDone }) {
  const [form, setForm] = useState({ name: "", code: "", active: true });
  useEffect(() => {
    if (open) setForm(edit ? { name: edit.name, code: edit.code, active: edit.active } : { name: "", code: "", active: true });
  }, [open, edit]);

  const save = async () => {
    if (!form.name.trim() || !form.code.trim()) { toast.error("Nama dan kode divisi wajib diisi"); return; }
    try {
      if (edit) await api.put(`/rab/divisions/${edit.id}`, form);
      else await api.post("/rab/divisions", form);
      toast.success("Divisi disimpan"); setOpen(false); onDone();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent>
        <DialogHeader><DialogTitle className="font-heading text-cocoa">{edit ? "Edit" : "Tambah"} Divisi</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><Label>Nama *</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="division-name-input" /></div>
          <div><Label>Kode *</Label><Input value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })} data-testid="division-code-input" /></div>
          <div className="flex items-center gap-2"><Switch checked={form.active} onCheckedChange={(v) => setForm({ ...form, active: v })} data-testid="division-active-switch" /><Label>Aktif</Label></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)} className="border-taupe text-cocoa">Batal</Button>
          <Button onClick={save} data-testid="save-division-btn" className="bg-cocoa hover:bg-cocoa-dark text-ivory">Simpan</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function UserDialog({ open, setOpen, edit, roles, divisions, permKeys = [], onDone }) {
  const [form, setForm] = useState({});
  useEffect(() => {
    if (open) setForm(edit ? { name: edit.name, email: edit.email || "", username: edit.username || "", role: edit.role, division_id: edit.division_id || "", active: edit.active, local_only: !!edit.local_only, single_device: !!edit.single_device, password: "", extra_roles: edit.extra_roles || [], perm_overrides: edit.perm_overrides || { allow: [], deny: [] } } : { name: "", email: "", username: "", role: "viewer", division_id: "", password: "", active: true, local_only: false, single_device: false, extra_roles: [], perm_overrides: { allow: [], deny: [] } });
  }, [open, edit]);

  const save = async () => {
    if (!form.name?.trim()) { toast.error("Nama wajib diisi"); return; }
    if (!form.email?.trim()) { toast.error("Email wajib diisi"); return; }
    if (!edit && !form.division_id) { toast.error("Divisi wajib dipilih untuk akun baru"); return; }
    try {
      if (edit) {
        const payload = { name: form.name, email: form.email, username: form.username || "", role: form.role, division_id: form.division_id, active: form.active, local_only: !!form.local_only, single_device: !!form.single_device, extra_roles: form.extra_roles || [], perm_overrides: form.perm_overrides || { allow: [], deny: [] } };
        if (form.password) payload.password = form.password;
        await api.put(`/users/${edit.id}`, payload);
      } else {
        await api.post("/users", form);
      }
      toast.success("Pengguna disimpan"); setOpen(false); onDone();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent>
        <DialogHeader><DialogTitle className="font-heading text-cocoa">{edit ? "Edit" : "Tambah"} Pengguna</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><Label>Nama *</Label><Input value={form.name || ""} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="user-name-input" /></div>
          <div><Label>Email {edit ? "(login)" : "*"}</Label><Input type="email" value={form.email || ""} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="user-email-input" /></div>
          <div><Label>Username (login opsional)</Label><Input value={form.username || ""} onChange={(e) => setForm({ ...form, username: e.target.value })} placeholder="mis. budi (bisa dipakai login selain email)" data-testid="user-username-input" /></div>
          <div><Label>Peran *</Label>
            <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
              <SelectTrigger data-testid="user-role-select"><SelectValue /></SelectTrigger>
              <SelectContent>{roles.map((r) => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><Label>Divisi {edit ? "" : "*"}</Label><Select value={form.division_id} onValueChange={(v) => setForm({ ...form, division_id: v })}><SelectTrigger data-testid="user-division-select"><SelectValue placeholder="Pilih divisi" /></SelectTrigger><SelectContent>{divisions.filter((d) => d.active || d.id === form.division_id).map((d) => <SelectItem key={d.id} value={d.id}>{d.name}{!d.active ? " (nonaktif)" : ""}</SelectItem>)}</SelectContent></Select></div>
          <div><Label>{edit ? "Kata Sandi Baru (opsional)" : "Kata Sandi *"}</Label><Input type="password" value={form.password || ""} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="user-password-input" /></div>
          <div className="flex items-center gap-2"><Switch checked={form.active !== false} onCheckedChange={(v) => setForm({ ...form, active: v })} /><Label>Aktif</Label></div>
          <div className="flex items-center gap-2"><Switch checked={!!form.local_only} onCheckedChange={(v) => setForm({ ...form, local_only: v })} data-testid="user-local-only-switch" /><Label>Login lokal saja (bypass HR)</Label></div>
          <div className="flex items-center gap-2"><Switch checked={!!form.single_device} onCheckedChange={(v) => setForm({ ...form, single_device: v })} data-testid="user-single-device-switch" /><Label>Kunci 1 perangkat (login baru mengeluarkan sesi lama)</Label></div>
          <div className="border-t border-taupe/20 pt-3" data-testid="user-overrides">
            <Label>Permission override</Label>
            <p className="text-[11px] text-wmuted mb-2">Allow menambah izin ekstra; Deny mencabut izin (Deny menang). Hanya System Owner.</p>
            <datalist id="user-perm-list">{permKeys.map((k) => <option key={k} value={k} />)}</datalist>
            {["allow", "deny"].map((kind) => (
              <div key={kind} className="mb-2">
                <div className="text-xs font-semibold text-cocoa mb-1">{kind === "allow" ? "Allow (tambah)" : "Deny (cabut)"}</div>
                <div className="flex flex-wrap gap-1 mb-1">
                  {((form.perm_overrides || {})[kind] || []).map((k) => (
                    <span key={k} className="text-[10px] bg-taupe/20 text-cocoa px-1.5 py-0.5 rounded inline-flex items-center gap-1" data-testid={`ov-${kind}-chip-${k}`}>{k}<button type="button" onClick={() => setForm({ ...form, perm_overrides: { ...form.perm_overrides, [kind]: form.perm_overrides[kind].filter((x) => x !== k) } })}>×</button></span>
                  ))}
                </div>
                <Input list="user-perm-list" placeholder={`Tambah ${kind}… (mis. finance.expense.approve)`} className="h-8" data-testid={`ov-${kind}-input`}
                  onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); const v = e.target.value.trim(); if (v && permKeys.includes(v) && !(form.perm_overrides[kind] || []).includes(v)) { setForm({ ...form, perm_overrides: { ...form.perm_overrides, [kind]: [...(form.perm_overrides[kind] || []), v] } }); } e.target.value = ""; } }} />
              </div>
            ))}
          </div>
          <div className="border-t border-taupe/20 pt-3" data-testid="user-extra-roles">
            <div className="flex items-center justify-between mb-1"><Label>Role tambahan (berbatas waktu)</Label>
              <Button type="button" size="sm" variant="outline" className="h-7 border-taupe text-cocoa" onClick={() => setForm({ ...form, extra_roles: [...(form.extra_roles || []), { role_key: "", expires_at: "", reason: "" }] })} data-testid="add-extra-role-btn"><Plus className="w-3.5 h-3.5 mr-1" />Tambah</Button>
            </div>
            <p className="text-[11px] text-wmuted mb-2">Hanya System Owner yang dapat mengatur. Role tambahan otomatis nonaktif setelah tanggal berakhir.</p>
            {(form.extra_roles || []).map((er, i) => (
              <div key={i} className="flex items-center gap-2 mb-2" data-testid={`extra-role-row-${i}`}>
                <Select value={er.role_key} onValueChange={(v) => { const a = [...form.extra_roles]; a[i] = { ...a[i], role_key: v }; setForm({ ...form, extra_roles: a }); }}>
                  <SelectTrigger className="h-8 flex-1" data-testid={`extra-role-select-${i}`}><SelectValue placeholder="Pilih role" /></SelectTrigger>
                  <SelectContent>{roles.map((r) => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}</SelectContent>
                </Select>
                <Input type="date" className="h-8 w-36" value={er.expires_at || ""} onChange={(e) => { const a = [...form.extra_roles]; a[i] = { ...a[i], expires_at: e.target.value }; setForm({ ...form, extra_roles: a }); }} data-testid={`extra-role-expiry-${i}`} />
                <Button type="button" size="icon" variant="ghost" className="h-8 w-8 text-status-reject" onClick={() => setForm({ ...form, extra_roles: form.extra_roles.filter((_, j) => j !== i) })} data-testid={`remove-extra-role-${i}`}>×</Button>
              </div>
            ))}
          </div>
          {edit && <EffectiveAccessPanel userId={edit.id} />}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)} className="border-taupe text-cocoa">Batal</Button>
          <Button onClick={save} data-testid="save-user-btn" className="bg-cocoa hover:bg-cocoa-dark text-ivory">Simpan</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


function EffectiveAccessPanel({ userId }) {
  const [d, setD] = useState(null);
  const [loading, setLoading] = useState(false);
  const load = () => { setLoading(true); api.get(`/users/${userId}/effective-access`).then((r) => setD(r.data)).catch((e) => toast.error(formatApiError(e.response?.data?.detail))).finally(() => setLoading(false)); };
  const sections = {};
  (d?.menus || []).forEach((m) => { (sections[m.section] = sections[m.section] || []).push(m); });
  return (
    <div className="border-t border-taupe/20 pt-3" data-testid="user-effective-access">
      <div className="flex items-center justify-between mb-1">
        <Label>Hak Akses Efektif (menu yang benar-benar terlihat user)</Label>
        <Button type="button" size="sm" variant="outline" className="h-7 border-taupe text-cocoa" onClick={load} data-testid="load-effective-access-btn">{loading ? "Memuat…" : d ? "Muat ulang" : "Lihat"}</Button>
      </div>
      {d && (
        <div className="space-y-2 text-sm">
          <div className="text-xs text-wmuted">Role utama: <b className="text-cocoa">{d.role_label}</b>{d.is_owner ? " (akses penuh)" : ""} · {d.menu_count} menu terlihat</div>
          {(d.extra_roles || []).length > 0 && (
            <div className="text-xs flex flex-wrap gap-1 items-center">Role tambahan:
              {d.extra_roles.map((er, i) => (
                <span key={i} className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded ${er.active ? "bg-emerald-100 text-emerald-700" : "bg-status-reject/15 text-status-reject"}`} data-testid={`ea-extra-${er.role_key}`}>{er.label}{er.expires_at ? ` s/d ${er.expires_at}` : ""} · {er.active ? "AKTIF" : "KADALUARSA"}</span>
              ))}
            </div>
          )}
          <div className="max-h-52 overflow-y-auto border border-taupe/15 rounded-lg p-2 space-y-2">
            {Object.keys(sections).map((sec) => (
              <div key={sec}>
                <div className="text-[11px] font-semibold text-wmuted uppercase">{sec}</div>
                <div className="flex flex-wrap gap-1 mt-1">
                  {sections[sec].map((m) => (
                    <span key={m.key} className="text-[11px] bg-taupe/15 text-cocoa px-1.5 py-0.5 rounded inline-flex items-center gap-1" data-testid={`ea-menu-${m.key}`}>
                      {m.label}{m.edit ? " ✎" : ""}{m.source === "extra" ? <span className="text-[9px] text-amber-700">(dari role tambahan)</span> : null}
                    </span>
                  ))}
                </div>
              </div>
            ))}
            {!(d.menus || []).length && <div className="text-xs text-wmuted">Tidak ada menu yang terlihat.</div>}
          </div>
        </div>
      )}
    </div>
  );
}

